import type {
    EcoTagType,
    CommonFinanceInfoType,
    FilterAssetType,
    PriceType,
    NewCarResultPromotionType,
    CarModelLabelType,
} from "../../../../../common-deprecated/types/CommonTypes";
import {
    type NewCarFuelTypeFilterId,
    defaultFinanceInfoObject,
    getDefaultPriceObject,
} from "../../../../../common-deprecated/constants";

// ----------------------------------------------------------------------
// Filter value constants, used across new car filter components
// ----------------------------------------------------------------------
export type CarFilterValuesType = {
    luggage: number;
    co2: number;
    fuelConsumption: number;
    speed: number;
    powerOutput: number;
    seats: number;
    doors: number;
    carType: string[];
    // Technically a car only has one engine and one fuel type so fuelType will almost always contain a single value.
    // The exception is plugin hybrid vehicles, the newCarFuelTypeFilterIds.plugin value is then added manually during formatting
    fuelType: NewCarFuelTypeFilterId[];
    equipment: string[];
    // Proace filter values.
    bodyType: string;
    wheelBase: string;
    eta: number;
    vehicleDimensions: {
        length: number;
        width: number;
        height: number;
    };
    towingCapacity: number;
    loadVolume: number;
    maximumPayload: number;
    transmission?: string[];
    wheeldrive?: string[];
};
export const defaultCarFilterValues: CarFilterValuesType = {
    luggage: 0,
    co2: 0,
    fuelConsumption: 0,
    speed: 0,
    powerOutput: 0,
    seats: 0,
    doors: 0,
    carType: [],
    fuelType: [],
    equipment: [],
    eta: 0,
    // Proace filter values.
    bodyType: "",
    wheelBase: "",
    towingCapacity: 0,
    loadVolume: 0,
    vehicleDimensions: {
        length: 0,
        width: 0,
        height: 0,
    },
    maximumPayload: 0,
};

// ----------------------------------------------------------------------
// Base car constants.
// ----------------------------------------------------------------------

export type CarGradeResultType = {
    id: string;
    name: string;
    features: string[];
    featuresText: string;
    colourCount: number;
    ecoTag: EcoTagType | undefined;
    availableForOnlineSales: boolean;
    availableForOnlineLeasing: boolean;
};
export const defaultCarGrade: CarGradeResultType = {
    id: "",
    name: "",
    features: [],
    featuresText: "",
    colourCount: 0,
    ecoTag: undefined,
    availableForOnlineSales: false,
    availableForOnlineLeasing: false,
};

type ProaceDimensionType = { id: string; name: string; value: string; unit: string };

export type NewCarResultType = {
    id: string;
    carShortId?: number;
    show: boolean;
    visibleInTopSpin: boolean;
    model: { id: string; code: string; name: string; labels: CarModelLabelType[] };
    submodelId: string;
    price: PriceType;
    finance: CommonFinanceInfoType;
    colour: string; // hex value of visual-for-grade colour if found, else the default colour
    colourId: string; // id of the colour property, or the default colour if setting newCarUseDefaultColourUrl is enabled
    colourCount: number;
    colourCode: string; // internalCode of the colour property
    rating: { total: number; value: number; range: number };
    eta: string;
    efficiencyClass: string; // ENERGY-EFFICIENCY-CLASS spec in CarDB
    promotions: NewCarResultPromotionType[];
    grade: CarGradeResultType;
    bodyType: { id: string; name: string };
    engine: {
        id: string;
        name: string;
        transmission: { id: string; name: string };
        wheeldrive: { id: string; name: string };
    };
    filterValues: CarFilterValuesType;
    // Used for Proace(verso) cars.
    dimensions: { capacity: ProaceDimensionType[]; dimension: ProaceDimensionType[] } | null;
    assets?: FilterAssetType[];
    soldOut: boolean;
};

export const defaultNewCarResult: NewCarResultType = {
    id: "",
    show: true,
    visibleInTopSpin: false,
    model: { id: "", code: "", name: "", labels: [] },
    submodelId: "",
    price: { ...getDefaultPriceObject() },
    finance: { ...defaultFinanceInfoObject },
    colour: "FFFFFF",
    colourId: "",
    colourCount: 0,
    colourCode: "",
    rating: { total: 0, value: 0, range: 5 },
    eta: "",
    efficiencyClass: "",
    promotions: [],
    grade: { ...defaultCarGrade },
    bodyType: { id: "", name: "" },
    engine: { id: "", name: "", transmission: { id: "", name: "" }, wheeldrive: { id: "", name: "" } },
    filterValues: { ...defaultCarFilterValues },
    dimensions: null,
    soldOut: false,
};

// ----------------------------------------------------------------------
// Model result constants
// ----------------------------------------------------------------------
export type ModelFilterValuesType = {
    fuelType: NewCarFuelTypeFilterId[];
    carType: string[];
    transmission: string[];
    wheeldrive: string[];
    equipment: string[];
};

export type ModelResultType = {
    id: string;
    name: string;
    code?: string;
    visibleInTopSpin?: boolean; // Property that decides if a car image is shown with or without a top
    // Duplicate car from carsarray with cheapest car / used inside sorting and for displaying the cheapest car
    cheapestCar?: NewCarResultType;
    // Duplicate car from carsarray with cheapest car monthly / used inside sorting and for displaying the cheapest car
    cheapestCarMonthly?: NewCarResultType;
    // All the filterValues of all the cars together
    modelFilterValues?: ModelFilterValuesType;
    connected: boolean;
    cars: NewCarResultType[];
    assets?: FilterAssetType[];
    promotions?: NewCarResultPromotionType[];
};
export const defaultModelResult: ModelResultType = {
    id: "",
    name: "",
    connected: false,
    cars: [],
};
